import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Brand from '../components/Brand';
import ConsentCheckboxes from '../components/ConsentCheckboxes';
import {
  validateName,
  validateEmail,
  validatePhone,
  validatePassword,
  validateConsents,
} from '../utils/validators';

function Signup({ onSignup }) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [termsConsent, setTermsConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const clearFieldError = (field) =>
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });

  const runValidation = () => {
    const next = {};
    const nameErr = validateName(name);
    if (nameErr) next.name = nameErr;

    const emailErr = validateEmail(email);
    if (emailErr) next.email = emailErr;

    const phoneErr = validatePhone(phone);
    if (phoneErr) next.phone = phoneErr;

    const pwErr = validatePassword(password);
    if (pwErr) next.password = pwErr;

    if (!confirmPassword) {
      next.confirmPassword = 'Please confirm your password';
    } else if (confirmPassword !== password) {
      next.confirmPassword = 'Passwords do not match';
    }

    Object.assign(next, validateConsents({ smsConsent, termsConsent }));
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const next = runValidation();
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 400));
    setSubmitting(false);

    navigate('/verify-otp', {
      state: {
        flow: 'signup',
        identifier: phone.trim(),
        method: 'phone',
        userData: {
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          smsConsent,
          termsConsent,
        },
      },
    });
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <Brand />
        <h1 className="auth-title">Create your account</h1>
        <p className="auth-subtitle">Start building your Bluprint today</p>

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label className="form-label" htmlFor="signup-name">
              Full name<span className="required-star"> *</span>
            </label>
            <input
              id="signup-name"
              className={`form-input ${errors.name ? 'input-error' : ''}`}
              type="text"
              autoComplete="name"
              placeholder="Jordan Rivera"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                clearFieldError('name');
              }}
              aria-invalid={!!errors.name}
            />
            {errors.name && <div className="field-error">{errors.name}</div>}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="signup-email">
              Email address<span className="required-star"> *</span>
            </label>
            <input
              id="signup-email"
              className={`form-input ${errors.email ? 'input-error' : ''}`}
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                clearFieldError('email');
              }}
              aria-invalid={!!errors.email}
            />
            {errors.email && <div className="field-error">{errors.email}</div>}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="signup-phone">
              Phone number<span className="required-star"> *</span>
            </label>
            <input
              id="signup-phone"
              className={`form-input ${errors.phone ? 'input-error' : ''}`}
              type="tel"
              autoComplete="tel"
              placeholder="+1 555 000 0000"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                clearFieldError('phone');
              }}
              aria-invalid={!!errors.phone}
            />
            {errors.phone && <div className="field-error">{errors.phone}</div>}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="signup-password">
              Password<span className="required-star"> *</span>
            </label>
            <input
              id="signup-password"
              className={`form-input ${errors.password ? 'input-error' : ''}`}
              type="password"
              autoComplete="new-password"
              placeholder="Min 8 chars, upper, lower, number"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                clearFieldError('password');
                if (confirmPassword) clearFieldError('confirmPassword');
              }}
              aria-invalid={!!errors.password}
            />
            {errors.password && <div className="field-error">{errors.password}</div>}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="signup-confirm">
              Confirm password<span className="required-star"> *</span>
            </label>
            <input
              id="signup-confirm"
              className={`form-input ${errors.confirmPassword ? 'input-error' : ''}`}
              type="password"
              autoComplete="new-password"
              placeholder="Re-enter your password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                clearFieldError('confirmPassword');
              }}
              aria-invalid={!!errors.confirmPassword}
            />
            {errors.confirmPassword && (
              <div className="field-error">{errors.confirmPassword}</div>
            )}
          </div>

          <ConsentCheckboxes
            smsConsent={smsConsent}
            setSmsConsent={(v) => {
              setSmsConsent(v);
              clearFieldError('smsConsent');
            }}
            termsConsent={termsConsent}
            setTermsConsent={(v) => {
              setTermsConsent(v);
              clearFieldError('termsConsent');
            }}
            errors={errors}
          />

          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? 'Creating account…' : 'Create Account'}
          </button>
        </form>

        <div className="auth-links">
          Already have an account? <Link to="/login">Sign in</Link>
        </div>
      </div>
    </div>
  );
}

export default Signup;
